// app/javascript/components/ShippingFlow/pages/payment/AddNewUserForm.jsx
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '../../context/ThemeContext';
import useOrderApi from '../../hooks/useOrderApi';
import PhoneNumberField from '../../components/formfields/PhoneNumberField';
import RichSelectField from '../../components/formfields/RichSelectField';

/**
 * Form component for creating a new user as part of On Behalf Of flow
 */
const AddNewUserForm = ({ brandId, onUserCreated, onCancel, countries = [] }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { createUser } = useOrderApi();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryOfResidence: 'US',
    phoneNumber: '',
    howDidYouHear: ''
  });
  
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isMounted, setIsMounted] = useState(true);

  React.useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Default countries if none provided
  const defaultCountries = [
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  ];

  const countryOptions = countries.length > 0 ? countries : defaultCountries;

  const hearAboutOptions = [
    'Please Select',
    'Google Search',
    'Social Media',
    'Friend or Family',
    'Travel Agent',
    'Golf Course',
    'Other'
  ];

  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.countryOfResidence) {
      errors.countryOfResidence = 'Country is required';
    }
    
    if (!formData.phoneNumber || !formData.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required';
    }
    
    return errors;
  };

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [validationErrors]);

  const handlePhoneChange = useCallback((e164Number) => {
    handleFieldChange('phoneNumber', e164Number);
  }, [handleFieldChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        // Generate a temporary password or handle registration differently
        password: Math.random().toString(36).slice(-8),
        hasTempPassword: true,
        isActive: false,
        termsAccepted: false // User will accept terms later
      };
      
      const newUser = await createUser(
        formData.authToken,
        brandId,
        userData
      );

      if (newUser && (newUser.email || newUser.user_email)) {
        if('user_email' in newUser){
          // convert the behalf user format
          onUserCreated({ 
            ...newUser,
            id: newUser.user_id,
            first_name: newUser.first_name,
            last_name: newUser.last_name,
            email: newUser.user_email,
            phone_number: newUser.user_phone,
           });
        } else {
          onUserCreated(newUser);
        }
      } else {
        throw new Error('Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      let errorMessage = 'Failed to create user. Please try again.';
      
      try {
        const errorData = JSON.parse(error.message);
        if (errorData.errors) {
          errorMessage = Array.isArray(errorData.errors) 
            ? errorData.errors.join(', ')
            : errorData.errors;
        }
      } catch (e) {
        // Use default error message
      }
      
      setSubmitError(errorMessage);
    } finally {
      isMounted && setIsSubmitting(false);
    }
  };

  const labelClassName = clsx(
    'block text-sm font-medium mb-1',
    {
      'text-slate-300': isDark,
      'text-gray-700': !isDark
    }
  );

  const inputClassName = clsx(
    'w-full px-3 py-2 border rounded-lg text-sm',
    'focus:outline-none focus:ring-2 focus:ring-blue-500',
    {
      'bg-slate-700 border-slate-600 text-slate-200': isDark,
      'bg-white border-gray-300 text-gray-900': !isDark
    }
  );

  const errorClassName = 'mt-1 text-sm text-red-500';

  const isFormValid = formData.firstName && formData.lastName && formData.email &&
    formData.countryOfResidence && formData.phoneNumber && 
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);

  const buttonClassName = clsx(
    'flex-1 py-3 rounded-lg font-medium transition-all',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    {
      'bg-slate-900 hover:bg-slate-800 text-white focus:ring-slate-700': 
        isFormValid && !isSubmitting && !isDark,
      'bg-slate-700 hover:bg-slate-600 text-white focus:ring-slate-500': 
        isFormValid && !isSubmitting && isDark,
      'bg-gray-300 text-gray-500 cursor-not-allowed': 
        !isFormValid || isSubmitting
    }
  );

  const cancelButtonClassName = clsx(
    'px-6 py-3 rounded-lg font-medium transition-all',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    {
      'bg-slate-600 hover:bg-slate-700 text-white focus:ring-slate-500': isDark,
      'bg-gray-200 hover:bg-gray-300 text-gray-700 focus:ring-gray-400': !isDark,
      'opacity-50 cursor-not-allowed': isSubmitting
    }
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* First Name & Last Name */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClassName}>
            First name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => handleFieldChange('firstName', e.target.value)}
            className={clsx(
              inputClassName,
              validationErrors.firstName && 'border-red-500'
            )}
            placeholder="John"
            disabled={isSubmitting}
          />
          {validationErrors.firstName && (
            <p className={errorClassName}>{validationErrors.firstName}</p>
          )}
        </div>
        
        <div>
          <label className={labelClassName}>
            Last name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => handleFieldChange('lastName', e.target.value)}
            className={clsx(
              inputClassName,
              validationErrors.lastName && 'border-red-500'
            )}
            placeholder="Doe"
            disabled={isSubmitting}
          />
          {validationErrors.lastName && (
            <p className={errorClassName}>{validationErrors.lastName}</p>
          )}
        </div>
      </div>

      {/* Email */}
      <div>
        <label className={labelClassName}>
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => handleFieldChange('email', e.target.value)}
          className={clsx(
            inputClassName,
            validationErrors.email && 'border-red-500'
          )}
          placeholder="john.doe@example.com"
          disabled={isSubmitting}
        />
        {validationErrors.email && (
          <p className={errorClassName}>{validationErrors.email}</p>
        )}
      </div>

      {/* Country of Residence */}
      <div>
        <label className={labelClassName}>
          Country of residence <span className="text-red-500">*</span>
        </label>
        <RichSelectField
          id="country-of-residence"
          value={formData.countryOfResidence}
          options={countryOptions}
          useCustomDropdown={true}
          valueKey="code"
          placeholder="Select country"
          formatValue={(option) => ({
            key: option?.code,
            icon: option?.flag || null,
            label: option?.name || '',
            value: option?.code
          })}
          onChange={(event) => {
            const { value } = event.target;
            handleFieldChange('countryOfResidence', value);
          }}
          containerClassName="w-full"
          disabled={isSubmitting}
          error={validationErrors.countryOfResidence}
        />
        {validationErrors.countryOfResidence && (
          <p className={errorClassName}>{validationErrors.countryOfResidence}</p>
        )}
      </div>

      {/* Mobile Phone */}
      <PhoneNumberField
        label="Mobile phone"
        value={formData.phoneNumber}
        onChange={handlePhoneChange}
        defaultCountry={formData.countryOfResidence}
        placeholder="Enter phone number"
        disabled={isSubmitting}
        error={validationErrors.phoneNumber}
        required={true}
        validateOnBlur={false}
        validateFormat={false}
      />

      {/* How did you hear about us */}
      <div>
        <label className={labelClassName}>
          How did you first hear about us?
        </label>
        <select
          value={formData.howDidYouHear}
          onChange={(e) => handleFieldChange('howDidYouHear', e.target.value)}
          className={inputClassName}
          disabled={isSubmitting}
        >
          {hearAboutOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {/* Submit Error */}
      {submitError && (
        <div className={clsx(
          'p-3 rounded-lg border',
          {
            'bg-red-900/20 border-red-600 text-red-400': isDark,
            'bg-red-50 border-red-200 text-red-600': !isDark
          }
        )}>
          {submitError}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={!isFormValid || isSubmitting}
          className={buttonClassName}
        >
          {isSubmitting ? 'Creating User...' : 'Register'}
        </button>
        
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className={cancelButtonClassName}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

AddNewUserForm.propTypes = {
  /** Brand ID for user creation */
  brandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  
  /** Callback when user is successfully created */
  onUserCreated: PropTypes.func.isRequired,
  
  /** Optional cancel callback */
  onCancel: PropTypes.func,
  
  /** Countries list from API */
  countries: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    flag: PropTypes.string
  }))
};

AddNewUserForm.defaultProps = {
  countries: []
};

export default AddNewUserForm;